import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Star } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";

const PRODUCTS = [
  {
    id: 1,
    name: "Potato Chips",
    flavor: "Tomato Twist",
    tag: "Crispy & Tangy",
    category: "Chips",
    image: "/redchips.png",
    badge: "50% More Chips",
    badgeColor: "bg-red-500 text-white",
    cardBg: "from-red-50 to-orange-50",
  },
  {
    id: 2,
    name: "Chatpata Mix",
    flavor: "Namkeen",
    tag: "Spicy & Crunchy",
    category: "Namkeen",
    image: "/Chatpata-Bnbn_zpU.png",
    badge: "Bestseller",
    badgeColor: "bg-orange-500 text-white",
    cardBg: "from-blue-50 to-indigo-50",
  },
  {
    id: 3,
    name: "Aloo Bhujia",
    flavor: "Namkeen",
    tag: "Classic & Crispy",
    category: "Namkeen",
    image: "/Aloo-oUrpi2pO.png",
    badge: null,
    cardBg: "from-green-50 to-emerald-50",
  },
  {
    id: 4,
    name: "Salted Pipe",
    flavor: "Snack",
    tag: "Light & Crunchy",
    category: "Snacks",
    image: "/Salted-lkWK5iNw.png",
    badge: "₹5 Only",
    badgeColor: "bg-yellow-400 text-stone-900",
    cardBg: "from-yellow-50 to-amber-50",
  },
];

const CATEGORIES = ["All", "Chips", "Namkeen", "Snacks"];

const ProductCard = ({ product, index }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.9 }}
    transition={{ duration: 0.3, delay: index * 0.08 }}
    className="group relative bg-white rounded-3xl border border-orange-100 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden"
    data-testid={`product-card-${product.id}`}
  >
    {/* Badge */}
    {product.badge && (
      <span
        className={`absolute top-4 right-4 z-10 ${product.badgeColor} text-xs font-bold px-3 py-1 rounded-full shadow-sm`}
      >
        {product.badge}
      </span>
    )}

    {/* Image area */}
    <div
      className={`relative bg-gradient-to-br ${product.cardBg} h-60 flex items-center justify-center p-5 overflow-hidden`}
    >
      <img
        src={product.image}
        alt={product.name}
        className="h-52 w-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform duration-400"
      />
    </div>

    {/* Info */}
    <div className="p-5">
      <p className="text-xs text-orange-500 font-semibold uppercase tracking-wider">
        {product.flavor}
      </p>
      <h3 className="text-xl font-bold text-stone-900 mt-1">{product.name}</h3>
      <span className="inline-block bg-orange-100 text-orange-700 text-xs px-2.5 py-1 rounded-full font-semibold mt-2">
        {product.tag}
      </span>
      <div className="flex items-center gap-0.5 mt-3">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />
        ))}
        <span className="text-xs text-stone-400 ml-1 font-medium">5.0</span>
      </div>

      <button
        className="mt-4 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full py-2.5 font-semibold text-sm opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-300 shadow-md"
        data-testid={`view-details-${product.id}`}
      >
        <ShoppingCart size={15} />
        View Details
      </button>
    </div>
  </motion.div>
);

const Products = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [ref, isVisible] = useScrollReveal(0.1);

  const filtered =
    activeCategory === "All"
      ? PRODUCTS
      : PRODUCTS.filter((p) => p.category === activeCategory);

  return (
    <section id="products" className="py-24 bg-white" data-testid="products-section">
      <div
        ref={ref}
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-sm font-semibold text-orange-500 uppercase tracking-widest">
            Our Collection
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mt-2">
            Explore Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              Products
            </span>
          </h2>
          <p className="text-stone-500 mt-4 text-lg max-w-xl mx-auto">
            Crafted with authentic flavors and premium ingredients for every
            snacking moment.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-200 ${
                activeCategory === cat
                  ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/25 scale-105"
                  : "bg-orange-50 text-orange-700 hover:bg-orange-100 hover:scale-105"
              }`}
              data-testid={`filter-${cat.toLowerCase()}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <AnimatePresence>
            {filtered.map((product, idx) => (
              <ProductCard key={product.id} product={product} index={idx} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Products;
